// eslint-disable-next-line import/no-cycle
import { AppThunkAction } from 'store';
import { api } from 'shared/utils/api';
import { Action, Reducer } from 'redux';
import { ApiCollection, ApiError } from 'models/api';
import { Ticket, CcTicketsState, TicketsFilter } from 'models/state/tickets';

// -----------------
// ACTIONS - These are serializable (hence replayable) descriptions of state transitions.
// They do not themselves have any side-effects; they just describe something that is going to happen.

// Use @typeName and isActionType for type detection that works even after serialization/deserialization.
const CC_TICKETS_FETCH_REQUEST = 'CC_TICKETS_FETCH_REQUEST';
const CC_TICKETS_FETCH_SUCCESS = 'CC_TICKETS_FETCH_SUCCESS';
const CC_TICKETS_FETCH_FAILURE = 'CC_TICKETS_FETCH_FAILURE';

interface CcTicketsFetchRequestAction extends Action<string> {
  type: typeof CC_TICKETS_FETCH_REQUEST;
}

interface CcTicketsFetchSuccessAction extends Action<string> {
  type: typeof CC_TICKETS_FETCH_SUCCESS;
  payload: ApiCollection<Ticket>;
}

interface CcTicketsFetchFailureAction extends Action<string> {
  type: typeof CC_TICKETS_FETCH_FAILURE;
  payload: ApiError;
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
export type CcTicketsActions =
  | CcTicketsFetchRequestAction
  | CcTicketsFetchSuccessAction
  | CcTicketsFetchFailureAction;

// ----------------
// ACTION CREATORS - These are functions exposed to UI components that will trigger a state transition.
// They don't directly mutate state, but they can have external side-effects (such as loading data).
const actionCreators = {
  request: () =>
    ({
      type: CC_TICKETS_FETCH_REQUEST,
    } as CcTicketsFetchRequestAction),
  success: (data: ApiCollection<Ticket>) =>
    ({
      type: CC_TICKETS_FETCH_SUCCESS,
      payload: data,
    } as CcTicketsFetchSuccessAction),
  failure: (error: ApiError) =>
    ({
      type: CC_TICKETS_FETCH_FAILURE,
      payload: error,
    } as CcTicketsFetchFailureAction),
};

// ----------------
// THUNKS
export const thunks = {
  requestTickets:
    (
      filter: TicketsFilter = {
        page: 1,
        pageSize: 20,
        searchText: '',
        status: undefined,
      }
    ): AppThunkAction<CcTicketsActions> =>
    async (dispatch) => {
      dispatch(actionCreators.request());

      const qs = `?page=${filter.page}&pageSize=${filter.pageSize}`;

      api.get<ApiCollection<Ticket>>(`api/v1/tickets/ccd/${qs}`).then(
        (data) => {
          dispatch(actionCreators.success(data));
        },
        (error: ApiError) => {
          dispatch(actionCreators.failure(error));
        }
      );
    },
};

// ----------------
// REDUCER - For a given state and action, returns the new state. To support time travel, this must not mutate the old state.
const defaultState: CcTicketsState = {
  data: undefined,
  loading: false,
  error: undefined,
};

export const reducer: Reducer<CcTicketsState> = (
  state: CcTicketsState = defaultState,
  incomingAction: Action | undefined = undefined
): CcTicketsState => {
  const action = incomingAction as CcTicketsActions;

  switch (action.type) {
    case CC_TICKETS_FETCH_REQUEST:
      return {
        ...state,
        loading: true,
      };

    case CC_TICKETS_FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        data: action.payload,
        error: undefined,
      };

    case CC_TICKETS_FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        data: undefined,
        error: action.payload,
      };

    default:
      return state;
  }
};
